<template>
  <form
    v-if="userIs.dvmValuer"
    ref="referenceField"
    class="ref-search"
    :class="{ 'apply-shake': shake }"
    @submit.prevent="() => {}"
  >
    <re-input
      v-model="dvmRef"
      :disable="isLoading"
      placeholder="Go to DVM (enter ref)"
      size="sm"
    >
      <template #embed>
        <re-button
          icon="mdi-arrow-right"
          type="submit"
          color="main"
          stretch
          :disable="!dvmRef"
          :loading="isLoading"
          size="sm"
          @click="goToDvm($event)"
        />
      </template>
    </re-input>
  </form>
</template>

<script setup lang="ts">
import { nextTick, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import { queryKeyToUrl, ReApi, ReApiVersion, ReEndpoint } from '@/composables/query'
import { useConfig } from '@/composables/useConfig'
import { useCurUser } from '@/composables/useCurUser'
import axios from '@/plugins/axios'

const config = useConfig()

const shake = ref(false)
const referenceField = ref<HTMLFormElement | null>(null)
const dvmRef = ref('')
const isLoading = ref(false)

const route = useRoute()
const router = useRouter()

const { is: userIs } = useCurUser()

onMounted(() => {
  try {
    nextTick(() => {
      if (referenceField.value) {
        referenceField.value.addEventListener('animationend', () => {
          shake.value = false
        })
      }
    })
  }
  catch (e) {
    console.log({ e })
  }
})

function goToDvm({ metaKey: newTab }: { metaKey: boolean }) {
  if (!dvmRef.value) {
    return
  }

  if (route.name === 'valuation.dvm' && dvmRef.value === route.query.valuation_request_ref && !newTab) {
    shake.value = true
    return
  }

  const name = config.ENABLE_NEW_DVM ? 'valuation.valuer-app' : 'valuation.dvm'

  isLoading.value = true
  const { url } = queryKeyToUrl([ReApi.VALUATION, ReApiVersion.CURRENT, ReEndpoint.REQUEST, dvmRef.value])
  axios
    .get(url)
    .then(() => {
      if (newTab) {
        const routeData = router.resolve({
          name,
          query: {
            valuation_request_ref: dvmRef.value,
          },
        })
        window.open(routeData.href, '_blank')
        isLoading.value = false
        return
      }
      router.push({
        name,
        query: {
          valuation_request_ref: dvmRef.value,
        },
      })
    })
    .catch(() => {
      shake.value = true
    })
    .finally(() => {
      isLoading.value = false
    })
}
</script>

<style scoped>
@keyframes shake {
  10%,
  90% {
    transform: translate3d(-2px, 0, 0);
  }

  20%,
  80% {
    transform: translate3d(4px, 0, 0);
  }

  30%,
  50%,
  70% {
    transform: translate3d(-8px, 0, 0);
  }

  40%,
  60% {
    transform: translate3d(8px, 0, 0);
  }
}

.apply-shake {
  animation: shake 0.82s cubic-bezier(0.36, 0.07, 0.19, 0.97) both;
}

.ref-search {
  transition: all 0.3s ease-in-out;
  opacity: 1;
}
</style>
